import classnames from 'classnames';
import gsap from 'gsap';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { last } from 'lib/helpers';

import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { START_DATE } from 'contexts/CruiseSearchContext/constants';
import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';
import Carousel, { CAROUSEL_ITEM_CLASS, DAY_CLASS } from '../Carousel';

import ScrollerModule from 'components/Scroller/ScrollerModule';
import { FILTERS_MODAL } from 'components/Scroller/constants';
import { getDateFilters, makeInitialRangesFromStartDate, monthsAvailable } from '../../FiltersModal/filters/Dates/utils';

import { useTheme } from '@mui/material';
import { getDisableDay } from 'lib/promotions';
import remap from 'lib/remap';
import { getBrandUISettings } from 'lib/utilities/Brands';
import { DATE_RANGE, DATE_RANGE_DAY, DATE_RANGE_MONTHS_WRAP } from './constants';
import {
	Constrain,
	DayButton,
	DayLabel,
	DayOfWeekLabel,
	DaysOfWeekWrapper,
	HeightHiddenSpan,
	MonthLabel,
	MonthWrapper,
	PickerRow,
	PickerWrapper,
} from './styles';

const ScrollingModule = ({ children, index, isMobile, scrollerId = FILTERS_MODAL }) => {
	const elId = `date-range-item-${index}`;
	const moduleId = `scroller-date-range-${index}`;
	const [ref, setRef] = useState();

	useEffect(() => {
		setRef(document.getElementById(elId));
	}, [elId]);

	const onUpdate = useCallback(
		({ progress }) => {
			if (!ref || !isMobile) return;
			let remappedProgress = remap(progress, 0.1, 0.3, 0, 1);
			const x = remap(progress, 0.1, 0.25, 25, 0);
			if (index === 0 || index === 1 || index === 2) {
				remappedProgress = 1;
			}
			gsap.set(ref, { opacity: remappedProgress, x });
		},
		[ref, index, isMobile],
	);
	return (
		<ScrollerModule id={moduleId} scrollerId={scrollerId} onUpdate={onUpdate}>
			{children}
		</ScrollerModule>
	);
};

ScrollingModule.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	isMobile: PropTypes.bool,
	scrollerId: PropTypes.string,
};

const DayButtonRenderer = ({
	palette,
	uiSettings,
	day,
	dayIndex,
	promotions,
	today,
	promotionSelected,
	hoverDay,
	datesRangeStart,
	datesRangeEnd,
	getCountFacet,
	firstDay,
	lastDay,
	onDateRangeChange,
	setHoverDay,
	handleMouseOver,
}) => {
	const { label, ariaLabel, ariaHidden, date, code } = day;
	const as = ariaHidden ? 'span' : undefined;
	const type = ariaHidden ? undefined : 'button';

	const unavailable = getDisableDay({ promotions, date, unavailable: date < today.current, promotionSelected });

	const start = (hoverDay?.date < datesRangeStart?.date && hoverDay?.date) || datesRangeStart?.date;
	const end = datesRangeEnd?.date || (hoverDay?.date === start && datesRangeStart?.date) || (hoverDay?.date > start && hoverDay?.date);
	const first = start === date;
	const last = end === date;
	const disabled = !!ariaHidden || datesRangeStart?.date === date || datesRangeEnd?.date === date;
	const selected = !disabled && date > start && date < end && getCountFacet(code) > 0;

	const disabledFacet = disabled ? false : !(getCountFacet(code) > 0);

	const classes = classnames({
		[DAY_CLASS]: !disabled,
		unavailable,
		disabled,
		selected,
		first,
		last,
		[DATE_RANGE_DAY]: true,
		'disabled-facet': disabledFacet,
	});

	const { dayOfWeek } = day;
	const isFirstDay = day?.code == firstDay?.code;
	const isLastDay = day?.code == lastDay?.code;
	const isSunday = dayOfWeek == 0;
	const isSaturday = dayOfWeek == 6;

	const onClickHandler = () => {
		onDateRangeChange(day);
		setHoverDay({});
	};

	const onMouseOverHandler = () => {
		handleMouseOver(day, disabled, start);
	};

	return (
		<DayButton
			isSunday={isSunday}
			isSaturday={isSaturday}
			isFirstDay={isFirstDay}
			isLastDay={isLastDay}
			as={as}
			type={type}
			key={ariaLabel || dayIndex}
			aria-label={ariaLabel}
			ariaLabel={ariaLabel}
			aria-hidden={ariaHidden}
			className={classes}
			onClick={onClickHandler}
			data-date={date}
			disabled={disabled}
			onMouseOver={onMouseOverHandler}
			palette={palette}
			weekDayUi={uiSettings.dateFilter.dateRange.weekDay}
			dayButtonUi={uiSettings.dateFilter.dateRange.dayButton}
		>
			<HeightHiddenSpan arid-hidden={true}>O</HeightHiddenSpan>
			{label && (
				<DayLabel variant="calendar" palette={palette} dayLabelUi={uiSettings.dateFilter.dateRange.dayLabel}>
					{' '}
					{label}{' '}
				</DayLabel>
			)}
		</DayButton>
	);
};

DayButtonRenderer.propTypes = {
	palette: PropTypes.object,
	uiSettings: PropTypes.object,
	day: PropTypes.object,
	dayIndex: PropTypes.number,
	promotions: PropTypes.object,
	today: PropTypes.any,
	promotionSelected: PropTypes.object,
	hoverDay: PropTypes.object,
	datesRangeStart: PropTypes.array,
	datesRangeEnd: PropTypes.array,
	getCountFacet: PropTypes.func,
	firstDay: PropTypes.object,
	lastDay: PropTypes.object,
	onDateRangeChange: PropTypes.func,
	setHoverDay: PropTypes.func,
	handleMouseOver: PropTypes.func,
};

/**
 * DateRange is a controlled calendar component.
 * Displays days (grouped by month, year) within the startDate facets returned from the graph.
 * Sets/listens to the startDate property of searchSelecting.
 * @param {array} range Controlled start, end range value objects
 * @param {function} onChange Controlled onChange handler
 * @param {number} currentIndex Used to control the carousel when screen size above small
 * @param {function} setCurrentIndex Function controlling carousel index
 * @param {string} scrollerId The id of the component's parent scroller
 *
 * @param mockToday Used for stories/testing out of context
 * @param mockCalendar Used for stories/testing out of context
 * @param mockDaysOfWeek Used for stories/testing out of context
 */
function DateRange({ datesRef, mockToday, mockCalendar, mockDaysOfWeek, scrollerId = FILTERS_MODAL, active, tabs }) {
	const {
		dateRangeCalendar = [],
		daysOfWeek = [],
		monthsCalendar = [],
		facetOptions,
		setAppliedDateFilter,
		updateDatesSelectingFilter,
		updateSearchSelectingFilter,
		datesSelecting,
		searchSelecting,
		fetchAndUpdateFacetOptions,
		brand,
		locale,
		promotions,
		promotionSelected,
	} = useContext(CruiseSearchContext);

	const { palette } = useTheme();
	const { uiSettings } = getBrandUISettings(brand);

	const calendar = mockCalendar || dateRangeCalendar;
	const days = mockDaysOfWeek || daysOfWeek;
	const { isMobile } = useContext(CruiseSelectionContext);
	const calendarRef = useRef();

	const today = useRef(mockToday || new Date());

	const classes = classnames({
		active,
		[DATE_RANGE]: true,
	});

	const getCountFacet = (monthCode) => facetOptions[START_DATE]?.[monthCode?.substring(0, 7)]?.count;

	const startDate = datesSelecting[START_DATE];

	const { initialDateRange, initialDateRangeIndex } = useMemo(
		() => {
			let dates = monthsAvailable(monthsCalendar, today, facetOptions);
			return makeInitialRangesFromStartDate(startDate, dateRangeCalendar, dates);
		},
		[dateRangeCalendar, facetOptions], // eslint-disable-line react-hooks/exhaustive-deps
	);

	const [hoverDay, setHoverDay] = useState({});
	const [datesRange, setDatesRange] = useState(initialDateRange);
	const [datesRangeStart, datesRangeEnd] = (datesRange.length && datesRange[0]) || [];
	const [currentDatesIndex, setCurrentDatesIndex] = useState(initialDateRangeIndex);

	const updateSearchFilters = (dateFilter, callback) => {
		updateDatesSelectingFilter(START_DATE, dateFilter);
		updateSearchSelectingFilter(START_DATE, dateFilter, callback);
	};

	const refreshMonthRange = (datesRange) => {
		const dateFilter = getDateFilters(datesRange, ['code'], [0, 1]);
		updateSearchFilters(dateFilter, fetchAndUpdateFacetOptions);
	};

	const onDateRangeChange = (day) => {
		let nextRange = [...datesRange];
		const [start, end] = nextRange?.[0] || [];

		if (!start || !end) {
			if (!start) {
				nextRange.push([day]);
				nextRange = [nextRange?.[0].sort((a, b) => a.date - b.date)];
			} else if (!end) {
				nextRange[0].push(day);
				nextRange = [nextRange?.[0].sort((a, b) => a.date - b.date)];
			}
		} else if (end) {
			nextRange = [[day]];
		}

		if (nextRange[0].length === 2 && nextRange[0][0].code > nextRange[0][1].code) {
			nextRange[0].reverse();
		}

		setDatesRange(nextRange);
		refreshMonthRange(nextRange);
		setAppliedDateFilter(tabs.dateRange.appliedDateFilter);
	};

	const resetHoverDay = () => {
		if (!hoverDay || Object.keys(hoverDay).length === 0) {
			return;
		}

		setHoverDay({});
	};

	const wrapClasses = classnames({
		[DATE_RANGE_MONTHS_WRAP]: true,
		[CAROUSEL_ITEM_CLASS]: true,
	});

	const getDateRangeLabel = (dateRangeLabel) => dateRangeLabel.replace(',', '');

	const handleMouseOver = useCallback(
		(day, disabled, start) => {
			if (disabled || (datesRangeStart && datesRangeEnd) || !start) {
				return;
			}
			setTimeout(() => {
				setHoverDay(day);
			}, 50);
		},
		[datesRangeStart, datesRangeEnd, hoverDay, setHoverDay],
	);

	useEffect(() => {
		const noStart = !datesRangeStart?.code;
		const noEnd = !datesRangeEnd?.code;
		if (noStart && noEnd) {
			updateSearchFilters([]);
		} else if (datesRangeStart?.code && !datesRangeEnd?.code) {
			return;
		}
	}, [datesRange]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!searchSelecting[START_DATE]?.length) {
			setCurrentDatesIndex(initialDateRangeIndex);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialDateRangeIndex]);

	const onResetDates = () => {
		setDatesRange([]);
	};

	useEffect(() => {
		datesRef.reset = onResetDates;
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Constrain className={classes} ref={calendarRef} onMouseLeave={resetHoverDay}>
			<Carousel currentIndex={currentDatesIndex} setCurrentIndex={setCurrentDatesIndex} onArrowsMouseEnter={resetHoverDay} isMobile={isMobile}>
				{calendar.map(({ dateRangeLabel, calendar, daysInMonth }, index) => {
					const elId = `date-range-item-${index}`;
					const [firstDay] = daysInMonth;
					const lastDay = last(daysInMonth);

					return (
						<ScrollingModule index={index} isMobile={isMobile} key={dateRangeLabel} scrollerId={scrollerId}>
							<MonthWrapper index={index} id={elId} passedKey={elId} className={wrapClasses}>
								<MonthLabel className="monthLabel" title={getDateRangeLabel(dateRangeLabel)} />
								<DaysOfWeekWrapper>
									{days.map((dayOfWeek) => {
										const key = `${dateRangeLabel}-day-${dayOfWeek}`;

										const dateDay = new Date(dayOfWeek);

										const dateUTC = new Date(dateDay.getUTCFullYear(), dateDay.getUTCMonth(), dateDay.getUTCDate());

										const label = dateUTC.toLocaleString(locale.language, {weekday: 'narrow'});
										return (
											<DayOfWeekLabel variant="calendarSemiBold" key={key} palette={palette} dayOfWeekUi={uiSettings.dateFilter.dateRange.dayOfWeek}>
												{label}
											</DayOfWeekLabel>
										);
									})}
								</DaysOfWeekWrapper>
								<PickerWrapper>
									{Object.values(calendar).map((column, i) => {
										const key = `${dateRangeLabel}-${column[0]?.dayOfWeek}-${i}`;
										return (
											<PickerRow key={key} dayButtonUi={uiSettings.dateFilter.dateRange.dayButton}>
												{column.map((day, dayIndex) => (
													<DayButtonRenderer
														key={day.code}
														palette={palette}
														uiSettings={uiSettings}
														day={day}
														dayIndex={dayIndex}
														promotions={promotions}
														today={today}
														promotionSelected={promotionSelected}
														hoverDay={hoverDay}
														datesRangeStart={datesRangeStart}
														datesRangeEnd={datesRangeEnd}
														getCountFacet={getCountFacet}
														firstDay={firstDay}
														lastDay={lastDay}
														onDateRangeChange={onDateRangeChange}
														setHoverDay={setHoverDay}
														handleMouseOver={handleMouseOver}
													/>
												))}
											</PickerRow>
										);
									})}
								</PickerWrapper>
							</MonthWrapper>
						</ScrollingModule>
					);
				})}
			</Carousel>
		</Constrain>
	);
}

DateRange.propTypes = {
	makeInitialRangesFromStartDate: PropTypes.func.isRequired,
	mockCalendar: PropTypes.array,
	mockDaysOfWeek: PropTypes.array,
	monthsAvailable: PropTypes.func.isRequired,
	scrollerId: PropTypes.string,
	active: PropTypes.bool,
	mockToday: PropTypes.any,
	tabs: PropTypes.object,
	datesRef: PropTypes.object.isRequired,
};

export default DateRange;
